var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React from 'react';
import { FlatList, View } from 'react-native';
import styled from 'styled-components/native';
import Skeleton from '@/components/Skeleton';
import { SccPressable } from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import useNavigation from '@/navigation/useNavigation';
import { openAppDeepLink } from '@/utils/appLinkNavigation';
import { isAppDeepLink } from '@/utils/deepLinkUtils';
const CARD_WIDTH = 130;
const CARD_HEIGHT = 160;
const CARD_GAP = 12;
const IMAGE_SIZE = Math.round(CARD_WIDTH * 0.55); // ~72px, scales with card
const CARD_BORDER_RADIUS = 12;
const CARD_BG_COLOR = '#f2f5fa';
const CardSeparator = () => _jsx(View, { style: { width: CARD_GAP } });
export default function RecommendedContentSection({ contents, isLoading, }) {
    if (contents.length === 0) {
        if (isLoading) {
            return (_jsxs(Container, { children: [_jsx(SectionTitle, { children: "\uC774\uB7F0 \uD0A4\uC6CC\uB4DC\uB294 \uC5B4\uB54C\uC694?" }), _jsxs(SkeletonRow, { children: [_jsx(Skeleton, { style: {
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    borderRadius: CARD_BORDER_RADIUS,
                                } }), _jsx(Skeleton, { style: {
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    borderRadius: CARD_BORDER_RADIUS,
                                } }), _jsx(Skeleton, { style: {
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    borderRadius: CARD_BORDER_RADIUS,
                                } })] })] }));
        }
        return null;
    }
    return (_jsx(LogParamsProvider, { params: { displaySectionName: 'recommended_content_section' }, children: _jsxs(Container, { children: [_jsx(SectionTitle, { children: "\uC774\uB7F0 \uD0A4\uC6CC\uB4DC\uB294 \uC5B4\uB54C\uC694?" }), _jsx(FlatList, { data: contents, horizontal: true, showsHorizontalScrollIndicator: false, contentContainerStyle: { paddingHorizontal: 20 }, ItemSeparatorComponent: CardSeparator, renderItem: ({ item, index }) => (_jsx(ContentCard, { content: item, index: index })), keyExtractor: item => item.id })] }) }));
}
function ContentCard({ content, index }) {
    const navigation = useNavigation();
    const openContent = () => __awaiter(this, void 0, void 0, function* () {
        const url = content.linkUrl;
        if (isAppDeepLink(url)) {
            openAppDeepLink(url, navigation);
        }
        else {
            navigation.navigate('Webview', {
                fixedTitle: content.title,
                url: url,
            });
        }
    });
    return (_jsx(SccPressable, { elementName: "home_v2_recommended_content", logParams: { content_id: content.id, index }, onPress: openContent, children: _jsxs(CardContainer, { children: [_jsxs(TextContainer, { children: [_jsx(ContentTitle, { numberOfLines: 1, children: content.title }), _jsx(ContentDescription, { numberOfLines: 2, children: content.description })] }), _jsx(ImageContainer, { children: _jsx(SccRemoteImage, { imageUrl: content.imageUrl, style: {
                            width: IMAGE_SIZE,
                            height: IMAGE_SIZE,
                            borderRadius: 0,
                            backgroundColor: CARD_BG_COLOR,
                        } }) })] }) }));
}
const Container = styled.View `
  padding: 20px 0;
  gap: 16px;
`;
const SectionTitle = styled.Text `
  color: #16181c;
  font-size: 20px;
  font-family: ${font.pretendardSemibold};
  line-height: 28px;
  letter-spacing: -0.4px;
  margin-horizontal: 20px;
`;
const CardContainer = styled.View `
  width: ${CARD_WIDTH}px;
  height: ${CARD_HEIGHT}px;
  border-radius: ${CARD_BORDER_RADIUS}px;
  background-color: ${CARD_BG_COLOR};
  padding: 14px;
  position: relative;
`;
const TextContainer = styled.View `
  gap: 6px;
`;
const ContentTitle = styled.Text `
  color: #121a27;
  font-size: 16px;
  font-family: ${font.pretendardBold};
  line-height: 24px;
  letter-spacing: -0.32px;
`;
const ContentDescription = styled.Text `
  color: ${color.gray50v2};
  font-size: 12px;
  line-height: 16px;
  font-family: ${font.pretendardRegular};
  letter-spacing: -0.24px;
`;
const ImageContainer = styled.View `
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: ${IMAGE_SIZE}px;
  height: ${IMAGE_SIZE}px;
  overflow: hidden;
`;
const SkeletonRow = styled.View `
  flex-direction: row;
  gap: 12px;
  padding-horizontal: 20px;
`;
